import { api, getApiError } from './api';
import { mapLeaveRequest, mapOvertimeRequest, mapSalaryAdvance, mapShiftSwapRequest, mapColleague } from './mappers';
import type { LeaveRequest, OvertimeRequest, SalaryAdvanceRequest, ShiftSwapRequest, Colleague, RequestStatus, ApiLeaveType } from '../types';

type PaginatedData = { data: Record<string, unknown>[] };

// ---------- Leave Types ----------

export async function getLeaveTypes(): Promise<ApiLeaveType[]> {
  const { data } = await api.get<{ data: ApiLeaveType[] }>('/me/leave/types');
  return data.data ?? [];
}

// ---------- Leave Requests ----------

export interface SubmitLeavePayload {
  leaveTypeId: number;
  startDate: string;
  endDate: string;
  reason: string;
}

export async function submitLeaveRequest(payload: SubmitLeavePayload): Promise<LeaveRequest> {
  try {
    const { data } = await api.post<{ data: Record<string, unknown> }>('/me/leave/requests', {
      leave_type_id: payload.leaveTypeId,
      start_date: payload.startDate,
      end_date: payload.endDate,
      reason: payload.reason,
    });
    return mapLeaveRequest(data.data);
  } catch (err) {
    throw new Error(getApiError(err));
  }
}

export async function getLeaveRequests(): Promise<LeaveRequest[]> {
  const { data } = await api.get<{ data: PaginatedData }>('/me/leave/requests', {
    params: { per_page: 100 },
  });
  return (data.data?.data ?? []).map(mapLeaveRequest);
}

export async function cancelLeaveRequest(id: string): Promise<void> {
  try {
    await api.post(`/me/leave/requests/${id}/cancel`);
  } catch (err) {
    throw new Error(getApiError(err));
  }
}

// ---------- Overtime Requests ----------

export interface SubmitOvertimePayload {
  date: string;
  totalHours: number;
}

export async function submitOvertimeRequest(payload: SubmitOvertimePayload): Promise<OvertimeRequest> {
  try {
    const { data } = await api.post<{ data: Record<string, unknown> }>('/me/overtime/requests', {
      date: payload.date,
      hours: payload.totalHours,
    });
    return mapOvertimeRequest(data.data);
  } catch (err) {
    throw new Error(getApiError(err));
  }
}

export async function getOvertimeRequests(): Promise<OvertimeRequest[]> {
  const { data } = await api.get<{ data: PaginatedData }>('/me/overtime/requests', {
    params: { per_page: 100 },
  });
  return (data.data?.data ?? []).map(mapOvertimeRequest);
}

export async function cancelOvertimeRequest(id: string): Promise<void> {
  try {
    await api.post(`/me/overtime/requests/${id}/cancel`);
  } catch (err) {
    throw new Error(getApiError(err));
  }
}

// ---------- Salary Advance Requests ----------

export interface SubmitSalaryAdvancePayload {
  amount: number;
  reason: string;
}

export async function submitSalaryAdvanceRequest(payload: SubmitSalaryAdvancePayload): Promise<SalaryAdvanceRequest> {
  try {
    const { data } = await api.post<{ data: Record<string, unknown> }>('/me/salary-advances', {
      amount: payload.amount,
      reason: payload.reason,
    });
    return mapSalaryAdvance(data.data);
  } catch (err) {
    throw new Error(getApiError(err));
  }
}

export async function getSalaryAdvanceRequests(): Promise<SalaryAdvanceRequest[]> {
  const { data } = await api.get<{ data: PaginatedData }>('/me/salary-advances', {
    params: { per_page: 100 },
  });
  return (data.data?.data ?? []).map(mapSalaryAdvance);
}

export async function cancelSalaryAdvanceRequest(id: string): Promise<void> {
  try {
    await api.post(`/me/salary-advances/${id}/cancel`);
  } catch (err) {
    throw new Error(getApiError(err));
  }
}

// ---------- Colleagues ----------

export async function getColleagues(swapDate?: string): Promise<Colleague[]> {
  const { data } = await api.get<{ data: Record<string, unknown>[] }>('/me/colleagues', {
    params: swapDate ? { swap_date: swapDate } : undefined,
  });
  return (data.data ?? []).map(mapColleague);
}

// ---------- Shift Swap Requests ----------

export interface SubmitShiftSwapPayload {
  targetId: number;
  swapDate: string;
  reason?: string;
}

export async function submitShiftSwapRequest(payload: SubmitShiftSwapPayload): Promise<ShiftSwapRequest> {
  try {
    const { data } = await api.post<{ data: Record<string, unknown> }>('/me/shift-swaps', {
      target_id: payload.targetId,
      swap_date: payload.swapDate,
      reason: payload.reason ?? null,
    });
    return mapShiftSwapRequest(data.data);
  } catch (err) {
    throw new Error(getApiError(err));
  }
}

export async function getShiftSwapRequests(): Promise<ShiftSwapRequest[]> {
  const { data } = await api.get<{ data: PaginatedData }>('/me/shift-swaps', {
    params: { per_page: 100 },
  });
  return (data.data?.data ?? []).map(mapShiftSwapRequest);
}

export async function cancelShiftSwapRequest(id: string): Promise<void> {
  try {
    await api.post(`/me/shift-swaps/${id}/cancel`);
  } catch (err) {
    throw new Error(getApiError(err));
  }
}

// ---------- Status badge variant ----------

export function getStatusVariant(status: RequestStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'approved': return 'default';
    case 'rejected': return 'destructive';
    case 'pending': return 'secondary';
    default: return 'outline';
  }
}

